import "core-js/modules/es.array.for-each";
import "core-js/modules/es.array.index-of";
import "core-js/modules/web.timers";
import tabbable from 'tabbable';
var listeningFocusTrap = null;

var isEscapeEvent = function isEscapeEvent(e) {
  return e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27;
};

var tryFocus = function tryFocus(_ref) {
  var node = _ref.node,
      _ref$delay = _ref.delay,
      delay = _ref$delay === void 0 ? 0 : _ref$delay;

  if (!node || !node.focus) {
    return;
  }

  setTimeout(function () {
    if (node.tagName.toLowerCase() === 'input') {
      node.select();
    }

    node.focus();
  }, delay);
}; // An alternative to tryFocus that deals with a problem with VoiceOver (iOS) in which VoiceOver
// does not follow focus correctly. See <AccessibilityFocus>.


var tryAccessibilityFocus = function tryAccessibilityFocus(_ref2) {
  var node = _ref2.node,
      _ref2$delay = _ref2.delay,
      delay = _ref2$delay === void 0 ? 100 : _ref2$delay;

  if (!node || !node.focus) {
    return;
  }

  setTimeout(function () {
    document.getElementById('accessibilityFocus').focus();
    setTimeout(function () {
      node.focus();
    }, delay);

    if (node.tagName.toLowerCase() === 'input') {
      node.select();
    }
  }, delay);
};

export default (function (element, userOptions) {
  var trap = {};
  var listeners = [];
  var config = userOptions || {};
  var onCreateFocusDelay = config.onCreateFocusDelay,
      onRemoveFocusDelay = config.onRemoveFocusDelay;
  var tabbableNodes = [];
  var nodeFocusedBeforeActivation = null;
  var active = false;
  var paused = false;
  var container = typeof element === 'string' ? document.querySelector(element) : element;
  config.returnFocusOnDeactivate = userOptions && userOptions.returnFocusOnDeactivate !== undefined ? userOptions.returnFocusOnDeactivate : true;
  config.escapeDeactivates = userOptions && userOptions.escapeDeactivates !== undefined ? userOptions.escapeDeactivates : true;

  var getNodeForOption = function getNodeForOption(optionName) {
    var optionValue = config[optionName];
    var node = optionValue;

    if (!optionValue) {
      return null;
    }

    if (typeof optionValue === 'string') {
      node = element.querySelector(optionValue);
    }

    if (typeof optionValue === 'function') {
      node = optionValue();

      if (!node) {
        throw new Error('`' + optionName + '` did not return a node');
      }
    }

    return node;
  };

  var firstFocusNode = function firstFocusNode() {
    var node;

    if (getNodeForOption('initialFocus') !== null) {
      node = getNodeForOption('initialFocus');
    } else if (container.contains(document.activeElement)) {
      node = document.activeElement;
    } else {
      node = tabbableNodes[0] || getNodeForOption('fallbackFocus');
    }

    if (!node) {
      throw new Error('You can\'t have a focus-trap without at least one focusable element');
    }

    return node;
  };

  var updateTabbableNodes = function updateTabbableNodes() {
    tabbableNodes = tabbable(container);
  };

  var removeListeners = function removeListeners() {
    if (!active || listeningFocusTrap !== trap) {
      return;
    }

    listeners.forEach(function (_ref3) {
      var event = _ref3.event,
          handler = _ref3.handler;
      return document.removeEventListener(event, handler, true);
    });
    listeningFocusTrap = null;
  };

  var deactivate = function deactivate(deactivateOptions) {
    if (!active) {
      return;
    }

    var defaultedDeactivateOptions = {
      returnFocus: deactivateOptions && deactivateOptions.returnFocus !== undefined ? deactivateOptions.returnFocus : config.returnFocusOnDeactivate,
      onDeactivate: deactivateOptions && deactivateOptions.onDeactivate !== undefined ? deactivateOptions.onDeactivate : config.onDeactivate
    };
    removeListeners();

    if (defaultedDeactivateOptions.onDeactivate) {
      defaultedDeactivateOptions.onDeactivate();
    }

    if (defaultedDeactivateOptions.returnFocus) {
      setTimeout(function () {
        return tryFocus({
          node: nodeFocusedBeforeActivation,
          delay: onRemoveFocusDelay
        });
      }, 0);
    }

    active = false;
    paused = false;
  };

  var handleTab = function handleTab(event) {
    if (!event.target.getAttribute('data-focustrap-ignore')) {
      event.preventDefault();
      updateTabbableNodes();
      var currentFocusIndex = tabbableNodes.indexOf(event.target);
      var lastTabbableNode = tabbableNodes[tabbableNodes.length - 1];
      var firstTabbableNode = tabbableNodes[0];

      if (event.shiftKey) {
        if (event.target === firstTabbableNode || tabbableNodes.indexOf(event.target) === -1) {
          tryFocus({
            node: lastTabbableNode
          });
        } else {
          tryFocus({
            node: tabbableNodes[currentFocusIndex - 1]
          });
        }
      }

      if (!event.shiftKey) {
        if (event.target === lastTabbableNode) {
          tryFocus({
            node: firstTabbableNode
          });
        } else {
          tryFocus({
            node: tabbableNodes[currentFocusIndex + 1]
          });
        }
      }
    }
  };

  var checkClick = function checkClick(e) {
    var masthead = document.getElementById('masthead');

    if (masthead && masthead.contains(e.target)) {
      return;
    }

    if (config.clickOutsideDeactivates) {
      return;
    }

    if (container && container.contains(e.target)) {
      return;
    }

    if (config.ignoreOutsideClicks) {
      return;
    }

    e.preventDefault();
    e.stopImmediatePropagation();
  }; // This needs to be done on mousedown and touchstart instead of click
  // so that it precedes the focus event


  var checkPointerDown = function checkPointerDown(e) {
    if (config.clickOutsideDeactivates) {
      return;
    }

    if (config.clickOutsideDeactivates && !container.contains(e.target)) {
      deactivate({
        returnFocus: false
      });
    }
  };

  var checkFocus = function checkFocus(e) {
    if (container.contains(e.target)) {
      return;
    }

    e.preventDefault();
    e.stopImmediatePropagation(); // Checking for a blur method here resolves a Firefox issue (#15)

    if (typeof e.target.blur === 'function') {
      e.target.blur();
    }
  };

  var checkKey = function checkKey(e) {
    if (e.key === 'Tab' || e.keyCode === 9) {
      !config.ignoreFocusTrapTabs && handleTab(e);
    }

    if (config.escapeDeactivates !== false && isEscapeEvent(e)) {
      deactivate();
    }
  };

  var addListeners = function addListeners() {
    if (!active) {
      return;
    } // There can be only one listening focus trap at a time


    if (listeningFocusTrap) {
      listeningFocusTrap.pause();
    }

    listeningFocusTrap = trap;
    updateTabbableNodes();

    if (!userOptions.skipFocusFirst) {
      if (userOptions.useAccessibilityFocus) {
        tryAccessibilityFocus({
          node: firstFocusNode(),
          delay: onCreateFocusDelay
        });
      } else {
        tryFocus({
          node: firstFocusNode(),
          delay: onRemoveFocusDelay
        });
      }
    }

    listeners.forEach(function (_ref4) {
      var event = _ref4.event,
          handler = _ref4.handler;
      return document.addEventListener(event, handler, true);
    });
  };

  var activate = function activate(activateOptions) {
    if (active) {
      return;
    }

    var defaultedActivateOptions = {
      onActivate: activateOptions && activateOptions.onActivate !== undefined ? activateOptions.onActivate : config.onActivate
    };
    active = true;
    paused = false;
    nodeFocusedBeforeActivation = document.activeElement;

    if (defaultedActivateOptions.onActivate) {
      defaultedActivateOptions.onActivate();
    }

    listeners.push({
      event: 'focus',
      handler: checkFocus
    }, {
      event: 'click',
      handler: checkClick
    }, {
      event: 'mousedown',
      handler: checkPointerDown
    }, {
      event: 'touchstart',
      handler: checkPointerDown
    }, {
      event: 'keydown',
      handler: checkKey
    });
    addListeners();
  };

  var pause = function pause() {
    if (paused || !active) {
      return;
    }

    paused = true;
    removeListeners();
  };

  var unpause = function unpause() {
    if (!paused || !active) {
      return;
    }

    paused = false;
    addListeners();
  };

  trap.activate = activate;
  trap.deactivate = deactivate;
  trap.pause = pause;
  trap.unpause = unpause;
  return trap;
});